import React, { useMemo, useState } from 'react';
import { Box, Button, Checkbox, Group, ScrollArea, Stack, Text } from '@mantine/core';
import { IconX } from '@tabler/icons-react';

export type CheckboxOption = {
    value: string;
    label: string;
};

type Props = {
    title: string;
    options: CheckboxOption[];
    selected: string[];
    onChangeSelected: (ids: string[]) => void;

    // Optional behavior
    maxHeight?: number | string;
    showReset?: boolean;
    onResetAll?: () => void;

    // Optional UI texts
    emptyText?: string;
};

export const CheckboxFilterSidebar: React.FC<Props> = ({
                                                           title,
                                                           options,
                                                           selected,
                                                           onChangeSelected,
                                                           maxHeight = '60vh',
                                                           showReset = false,
                                                           onResetAll,
                                                           emptyText = 'No matches',
                                                       }) => {
    // NOTE: Query is kept for future; can be wired to TextInput later.
    const [query] = useState('');

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return options;
        return options.filter((p) => p.label.toLowerCase().includes(q));
    }, [options, query]);

    const visibleValues = useMemo(() => filtered.map((p) => p.value), [filtered]);
    const allVisibleChecked =
        visibleValues.length > 0 && visibleValues.every((v) => selected.includes(v));

    const toggleOne = (value: string) => {
        if (selected.includes(value)) {
            onChangeSelected(selected.filter((v) => v !== value));
        } else {
            onChangeSelected([...selected, value]);
        }
    };

    // NOTE: Not used now, but left here because your old sidebar had it (useful later).
    const toggleAllVisible = () => {
        if (allVisibleChecked) {
            onChangeSelected(selected.filter((v) => !visibleValues.includes(v)));
        } else {
            const set = new Set([...selected, ...visibleValues]);
            onChangeSelected([...set]);
        }
    };

    const clearAll = () => {
        onChangeSelected([]);
        onResetAll?.();
    };

    return (
        <Box>
            <Stack gap="sm">
                <Text fw={600} size="lg">
                    {title}
                </Text>

                <ScrollArea.Autosize mah={maxHeight} type="auto" offsetScrollbars>
                    <Stack gap={6} pt={6}>
                        {filtered.map((p) => (
                            <Checkbox
                                key={p.value}
                                checked={selected.includes(p.value)}
                                onChange={() => toggleOne(p.value)}
                                label={p.label}
                                value={p.value}
                            />
                        ))}

                        {filtered.length === 0 && (
                            <Text size="sm" c="dimmed" ta="center" py="xs">
                                {emptyText}
                            </Text>
                        )}
                    </Stack>
                </ScrollArea.Autosize>

                <Group justify="apart" mt="sm">
                    {showReset && (
                        <Button variant="subtle" leftSection={<IconX size={16} />} onClick={clearAll}>
                            Reset
                        </Button>
                    )}
                </Group>
            </Stack>
        </Box>
    );
};
